import {PayloadAction, createSlice} from '@reduxjs/toolkit'
import {ZadSupplierPayment} from '../../../models/zad'
import {ZadApiError} from '../zad/errors'
import {
  cancelZadSupplierPayment,
  createZadSupplierPayment,
  fetchZadPaymentPickerPurchases,
  fetchZadPaymentPickerSuppliers,
  fetchZadPurchasePaymentSummary,
  fetchZadSupplierAccountSummary,
  fetchZadSupplierPaymentById,
  fetchZadSupplierPayments,
} from './service'
import {FetchZadSupplierPaymentsResponse, ZadSupplierPaymentState} from './types'

const initialState: ZadSupplierPaymentState = {
  payments: [],
  total: 0,
  totalPages: 0,
  listLoading: false,
  listInitialized: false,
  actionLoading: false,
  selectedPayment: null,
  supplierSummary: null,
  purchaseSummaries: {},
  pickerSuppliers: [],
  pickerPurchases: [],
  pickerLoading: false,
  error: null,
  errorCode: null,
  errorStatus: null,
}

function applyRejected(state: ZadSupplierPaymentState, action: PayloadAction<any>) {
  const payload = (action.payload || {}) as ZadApiError
  state.error = payload.error || 'Request failed'
  state.errorCode = payload.errorCode || null
  state.errorStatus = payload.status || null
}

function clearError(state: ZadSupplierPaymentState) {
  state.error = null
  state.errorCode = null
  state.errorStatus = null
}

function upsertPayment(state: ZadSupplierPaymentState, payment?: ZadSupplierPayment | null) {
  if (!payment?._id) return
  state.selectedPayment = payment
  const index = state.payments.findIndex((item) => item._id === payment._id)
  if (index >= 0) {
    state.payments[index] = payment
  }
}

const zadSupplierPaymentSlice = createSlice({
  name: 'zadSupplierPayments',
  initialState,
  reducers: {
    clearZadSupplierPaymentError: (state: ZadSupplierPaymentState) => {
      clearError(state)
    },
    clearSelectedZadSupplierPayment: (state: ZadSupplierPaymentState) => {
      state.selectedPayment = null
    },
    clearZadSupplierSummary: (state: ZadSupplierPaymentState) => {
      state.supplierSummary = null
    },
    clearZadPaymentPickerPurchases: (state: ZadSupplierPaymentState) => {
      state.pickerPurchases = []
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchZadSupplierPayments.pending, (state) => {
      state.listLoading = true
      clearError(state)
    })
    builder.addCase(fetchZadSupplierPayments.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.listLoading = false
      state.listInitialized = true
    })
    builder.addCase(
      fetchZadSupplierPayments.fulfilled,
      (state, action: PayloadAction<FetchZadSupplierPaymentsResponse>) => {
        state.listLoading = false
        state.listInitialized = true
        clearError(state)
        state.payments = action.payload.payments
        state.total = action.payload.total
        state.totalPages = action.payload.totalPages
      }
    )

    builder.addCase(fetchZadSupplierPaymentById.pending, (state) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadSupplierPaymentById.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadSupplierPaymentById.fulfilled, (state, action) => {
      state.actionLoading = false
      state.selectedPayment = action.payload.payment
    })

    builder.addCase(createZadSupplierPayment.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(createZadSupplierPayment.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(createZadSupplierPayment.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPayment(state, action.payload.payment)
    })

    builder.addCase(cancelZadSupplierPayment.pending, (state) => {
      state.actionLoading = true
      clearError(state)
    })
    builder.addCase(cancelZadSupplierPayment.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(cancelZadSupplierPayment.fulfilled, (state, action) => {
      state.actionLoading = false
      clearError(state)
      upsertPayment(state, action.payload.payment)
    })

    builder.addCase(fetchZadSupplierAccountSummary.pending, (state) => {
      state.actionLoading = true
    })
    builder.addCase(fetchZadSupplierAccountSummary.rejected, (state, action: PayloadAction<any>) => {
      applyRejected(state, action)
      state.actionLoading = false
    })
    builder.addCase(fetchZadSupplierAccountSummary.fulfilled, (state, action) => {
      state.actionLoading = false
      state.supplierSummary = action.payload.summary
    })

    builder.addCase(fetchZadPurchasePaymentSummary.fulfilled, (state, action) => {
      const summary = action.payload.summary
      if (summary?.purchaseId) {
        state.purchaseSummaries[summary.purchaseId] = summary
      }
    })

    builder.addCase(fetchZadPaymentPickerSuppliers.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadPaymentPickerSuppliers.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadPaymentPickerSuppliers.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerSuppliers = action.payload.suppliers
    })

    builder.addCase(fetchZadPaymentPickerPurchases.pending, (state) => {
      state.pickerLoading = true
    })
    builder.addCase(fetchZadPaymentPickerPurchases.rejected, (state) => {
      state.pickerLoading = false
    })
    builder.addCase(fetchZadPaymentPickerPurchases.fulfilled, (state, action) => {
      state.pickerLoading = false
      state.pickerPurchases = action.payload.purchases
    })
  },
})

export default zadSupplierPaymentSlice.reducer
export const actions = zadSupplierPaymentSlice.actions
export {initialState as zadSupplierPaymentInitialState}
